'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const serviceCategories = [
  { icon: 'WrenchScrewdriverIcon', label: 'Plumbing' },
  { icon: 'BoltIcon', label: 'Electrical' },
  { icon: 'WrenchIcon', label: 'Carpentry' },
  { icon: 'PaintBrushIcon', label: 'Painting' },
  { icon: 'SparklesIcon', label: 'Deep Cleaning' },
  { icon: 'CubeIcon', label: 'AC & HVAC' },
  { icon: 'ShieldCheckIcon', label: 'Security Systems' },
  { icon: 'ExclamationTriangleIcon', label: 'Pest Control' },
  { icon: 'TruckIcon', label: 'CarryMen / Moving' },
  { icon: 'WrenchScrewdriverIcon', label: 'Appliance Repair' },
  { icon: 'BuildingStorefrontIcon', label: 'Office Maintenance' },
  { icon: 'HomeModernIcon', label: 'Renovation & Fit-out' },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'DevicePhoneMobileIcon',
    label: 'Customer & Provider Apps (iOS & Android)',
    desc: 'Customers browse services, select time slots, and track workers live. Workers receive job alerts, navigate to sites, update job status, and capture photo proof — all from dedicated apps.',
    pills: ['Instant booking', 'Live worker tracking', 'In-app payments', 'Service history'],
  },
  {
    num: '02',
    icon: 'CalendarDaysIcon',
    label: 'Smart Scheduling & Slot Management',
    desc: 'Customers pick preferred time slots. The platform auto-assigns available workers based on skill, proximity, and current workload — with automatic reminders and rescheduling support.',
    pills: ['Slot blocking', 'Recurring bookings', 'Auto-reminders', 'Capacity control'],
  },
  {
    num: '03',
    icon: 'UserGroupIcon',
    label: 'Service Provider Onboarding & Verification',
    desc: 'Structured onboarding with document upload, background verification, skill categorisation, and training certification. New workers are live only after full compliance is cleared.',
    pills: ['Document verification', 'Skill tagging', 'Training tracker', 'Compliance alerts'],
  },
  {
    num: '04',
    icon: 'CurrencyRupeeIcon',
    label: 'Dynamic Pricing & Package Management',
    desc: 'Set service-wise pricing, build bundle packages, configure zone-based fares, enable surge pricing during peak demand, and offer corporate contracts — all from the admin panel.',
    pills: ['Bundle packages', 'Zone pricing', 'Surge control', 'Corporate rates'],
  },
  {
    num: '05',
    icon: 'StarIcon',
    label: 'Ratings, Reviews & Quality Control',
    desc: 'Post-service ratings, photo and video proof of completion, supervisor quality audits, and automated escalation for low-rated jobs ensure consistently high service standards.',
    pills: ['Photo proof', 'Supervisor audit', 'Auto-escalation', 'Dispute handling'],
  },
  {
    num: '06',
    icon: 'ChartBarIcon',
    label: 'Revenue Analytics & Provider Payouts',
    desc: 'Track revenue by service type, branch, and worker. Automated daily or weekly payouts to workers, commission deduction, GST invoicing, and exportable financial reports.',
    pills: ['Per-job earnings', 'Auto payout', 'GST invoicing', 'Branch analytics'],
  },
];

const workflowSteps = [
  { icon: 'DevicePhoneMobileIcon', title: 'Customer Books', desc: 'Via app, WhatsApp, or website. Service, slot, and address confirmed.' },
  { icon: 'BoltIcon', title: 'Job Assigned', desc: 'Auto-assigned to nearest verified worker based on skill and availability.' },
  { icon: 'MapPinIcon', title: 'Worker En Route', desc: 'Customer tracks worker live. ETA shared with push notification.' },
  { icon: 'ClipboardDocumentListIcon', title: 'Work in Progress', desc: 'Worker checks in on app. Job notes, material usage, and photos logged.' },
  { icon: 'DocumentCheckIcon', title: 'Job Completed', desc: 'Worker marks complete, uploads photo proof, and collects signature.' },
  { icon: 'CurrencyRupeeIcon', title: 'Invoice & Payment', desc: 'Digital invoice auto-generated. Payment collected, worker payout triggered.' },
];

const customerAppFeatures = [
  'Browse & Book All Facility Services',
  'Real-time Worker Live Tracking',
  'Time Slot Selection & Rescheduling',
  'In-app Chat with Worker',
  'Service History & Reorder',
  'Digital Invoice & Payment',
  'Rate & Review Every Job',
  'Multi-address Management',
  'Subscription & AMC Plans',
  'Refer & Earn Program',
  'Push & WhatsApp Notifications',
  'Dark Mode & Multi-language',
];

const workerAppFeatures = [
  'Accept / Reject Job Alerts',
  'GPS Navigation to Job Site',
  'Check-in / Check-out (Attendance)',
  'Live Job Status Updates',
  'Photo & Video Documentation',
  'Job Sheet & Work Notes',
  'Material Request & Usage Log',
  'Earnings & Payout Dashboard',
  'Daily Schedule & Route View',
  'Customer Call Masking',
  'Offline Mode Support',
  'Performance Score & Badges',
];

const managementFeatures = [
  { icon: 'ClipboardDocumentListIcon', label: 'Work Order Management', sub: 'Create, assign, and track all jobs' },
  { icon: 'UserGroupIcon', label: 'Attendance Tracking', sub: 'App check-in, GPS-verified, daily logs' },
  { icon: 'BuildingStorefrontIcon', label: 'Multi-branch Setup', sub: 'Separate teams, pricing & reports per branch' },
  { icon: 'DocumentTextIcon', label: 'Invoicing & Billing', sub: 'Auto GST invoices, credit notes, billing history' },
  { icon: 'CubeIcon', label: 'Inventory & Materials', sub: 'Track stock issued to each worker or job' },
  { icon: 'ChartBarIcon', label: 'SLA & KPI Dashboard', sub: 'On-time rate, job completion, escalations' },
  { icon: 'ShieldCheckIcon', label: 'Vendor Management', sub: 'Empanel, score, and pay external contractors' },
  { icon: 'AdjustmentsHorizontalIcon', label: 'Role-based Access', sub: 'HQ, branch manager, supervisor, worker roles' },
];

const vendorBenefits = [
  { icon: 'UserGroupIcon', title: 'Empanel External Contractors', desc: 'Register and verify third-party vendors with document upload, skill tags, and service area mapping.' },
  { icon: 'ClipboardDocumentListIcon', title: 'Assign Jobs to Vendors', desc: 'Route overflow jobs or specialised requests to empanelled vendors directly from the dispatcher panel.' },
  { icon: 'ChartBarIcon', title: 'Vendor Performance Tracking', desc: 'Monitor vendor ratings, on-time rates, and complaint history to maintain quality across your partner network.' },
  { icon: 'CurrencyRupeeIcon', title: 'Automated Vendor Payments', desc: 'Settle vendor invoices automatically on configurable cycles — weekly, fortnightly, or monthly — with full audit trails.' },
];

const multiBranchFeatures = [
  'Separate worker pools per branch',
  'Branch-level pricing and packages',
  'Branch-wise revenue and job reports',
  'Centralised HQ dashboard for all branches',
  'Branch manager access with scoped permissions',
  'Service zone configuration per branch',
  'Cross-branch job reassignment',
  'Consolidated invoicing and payouts',
];

const stats = [
  { value: '12+', label: 'Facility Service Categories', icon: 'WrenchScrewdriverIcon' },
  { value: 'Real-time', label: 'Worker Live Tracking', icon: 'MapPinIcon' },
  { value: 'Multi-branch', label: 'Operations Control', icon: 'BuildingStorefrontIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'BoltIcon' },
];

export default function FacilityProContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (teal gradient + service grid) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 55%, #0891B2 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 65%)', top: '-200px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.6) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/60 text-sm font-medium mb-7 hover:text-white transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-5">
                <Icon name="HomeModernIcon" size={11} />
                Facility Management
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Facility Pro —{' '}
                <span className="italic font-light" style={{ color: '#A5F3FC' }}>
                  All Facility Services,
                </span>
                <br />One Platform.
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/65 text-lg leading-relaxed mb-9 max-w-lg">
                Plumbing, electrical, cleaning, HVAC, security, carpentry — every facility service managed through one platform with worker tracking, attendance, multi-branch control, and automated invoicing.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/25 hover:border-white/50 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['21-day go-live', 'Multi-branch ready', 'iOS & Android apps', 'GST invoicing'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/50 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#A5F3FC]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — service categories grid */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl p-6 border border-white/15"
                style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
              >
                <p className="text-[11px] font-semibold text-white/40 uppercase tracking-widest mb-4">All Services on One Platform</p>
                <div className="grid grid-cols-3 gap-3">
                  {serviceCategories.map((s) => (
                    <div
                      key={s.label}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl border border-white/10 bg-white/[0.06] hover:bg-white/[0.12] transition-colors text-center group"
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/15 group-hover:scale-110 transition-transform">
                        <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={17} className="text-white" />
                      </div>
                      <span className="text-[11px] font-semibold text-white/80 leading-tight">{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-white/[0.06] border border-white/10">
                  <Icon name="PlusCircleIcon" size={16} className="text-[#A5F3FC] flex-shrink-0" />
                  <span className="text-xs text-white/60 font-medium">And any custom service category you need</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/60 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES (cards with sub-pills) ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that run{' '}
              <span className="italic font-light text-[#0E7490]">your entire facility business.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From customer booking to worker dispatch, invoicing, and payouts — all automated.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border hover:shadow-lg transition-all duration-300`}
                style={{
                  background: 'white',
                  borderColor: i % 2 === 0 ? 'rgba(14,116,144,0.15)' : 'rgba(15,23,42,0.07)',
                }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <span
                    className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #0E7490, #0891B2)' }}
                  >
                    {f.num}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(14,116,144,0.1)' }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0E7490]" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug pt-1 flex-1">{f.label}</h3>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{f.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {f.pills.map((p) => (
                    <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#0E7490]" style={{ background: 'rgba(14,116,144,0.08)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WORK FLOW (dark teal horizontal steps) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#0C3B47' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(14,116,144,0.4) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#A5F3FC]/30 bg-[#A5F3FC]/10 text-[11px] font-semibold tracking-widest text-[#A5F3FC] uppercase mb-5">
              Service Workflow
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              Booking to completion —{' '}
              <span className="italic font-light text-[#A5F3FC]">fully automated.</span>
            </h2>
          </div>

          {/* 6-step pipeline */}
          <div className="relative grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Connecting line (desktop) */}
            <div className="hidden lg:block absolute top-[26px] left-[calc(8.33%+20px)] right-[calc(8.33%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(165,243,252,0.05), rgba(165,243,252,0.3) 20%, rgba(165,243,252,0.3) 80%, rgba(165,243,252,0.05))' }} />

            {workflowSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #0E7490, #0891B2)', boxShadow: '0 0 24px rgba(14,116,144,0.4)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <div className="text-[10px] font-bold text-[#A5F3FC]/50 tracking-widest uppercase mb-1.5">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-1.5">{step.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 mt-14 flex flex-wrap justify-center gap-3">
            {['GPS-verified Check-in', 'Live Job Tracking', 'Photo Proof of Work', 'Auto Invoice', 'Digital Signature', 'Instant Payout'].map((p) => (
              <div key={p} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#A5F3FC]/20 bg-white/[0.04]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A5F3FC]" />
                <span className="text-sm text-white/60 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CUSTOMER APP & WORKER APP (2-col) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Mobile Apps
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Two apps. Built for{' '}
              <span className="italic font-light text-[#0E7490]">Customers & Workers.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Customer App */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(14,116,144,0.15)] shadow-sm">
              <div className="p-6" style={{ background: 'linear-gradient(135deg, #0E7490, #0891B2)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/60 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2" style={{ background: '#F0FDFA' }}>
                {customerAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-[rgba(14,116,144,0.08)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(14,116,144,0.12)' }}>
                      <Icon name="CheckIcon" size={10} className="text-[#0E7490]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Worker App */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm">
              <div className="p-6 bg-[#0C3B47]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon name="UserIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Worker App</h3>
                    <p className="text-white/50 text-xs">iOS & Android · For field technicians</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#F5F5F5]">
                {workerAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0C3B47]">
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MANAGEMENT DASHBOARD ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Operations Control
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Every operation.{' '}
              <span className="italic font-light text-[#0E7490]">One dashboard.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">From work orders and attendance to invoices and vendor management — your admin panel handles it all.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {managementFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group p-6 rounded-2xl bg-white border border-[rgba(14,116,144,0.1)] hover:border-[rgba(14,116,144,0.3)] hover:shadow-md transition-all duration-300`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: 'rgba(14,116,144,0.1)' }}
                >
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0E7490]" />
                </div>
                <p className="font-semibold text-sm text-[#0F172A] mb-1.5">{f.label}</p>
                <p className="text-xs text-[#64748B] leading-relaxed">{f.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MULTI-BRANCH SETUP ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-5">
                Multi-branch
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Scale across{' '}
                <span className="italic font-light text-[#0E7490]">multiple locations seamlessly.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Whether you operate from a single city or across multiple regions, Facility Pro lets you manage each branch independently — with its own team, pricing, and reporting — while giving headquarters a complete consolidated view.
              </p>
              <div className="flex flex-col gap-3">
                {multiBranchFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #0E7490, #0891B2)' }}
                    >
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — branch visual */}
            <div className="animate-on-scroll stagger-2 flex flex-col gap-4">
              {/* HQ card */}
              <div
                className="rounded-2xl p-6 text-white text-center"
                style={{ background: 'linear-gradient(135deg, #0C3B47, #0E7490)' }}
              >
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <Icon name="BuildingStorefrontIcon" size={22} className="text-white" />
                </div>
                <p className="font-semibold text-base">Headquarters</p>
                <p className="text-white/60 text-xs mt-1">Consolidated view of all branches, workers, and revenue</p>
              </div>

              {/* Branches */}
              <div className="grid grid-cols-3 gap-3">
                {['Branch — Chennai', 'Branch — Coimbatore', 'Branch — Bangalore'].map((b, i) => (
                  <div key={b} className="rounded-xl p-4 text-center border" style={{ background: '#F0FDFA', borderColor: 'rgba(14,116,144,0.2)' }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ background: 'rgba(14,116,144,0.12)' }}>
                      <Icon name="MapPinIcon" size={16} className="text-[#0E7490]" />
                    </div>
                    <p className="text-xs font-semibold text-[#0F172A] leading-snug">{b}</p>
                    <p className="text-[10px] text-[#64748B] mt-0.5">Own team · Own pricing</p>
                  </div>
                ))}
              </div>

              {/* Bottom metrics */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'EyeIcon', title: 'Unified Visibility', desc: 'All branches, one screen' },
                  { icon: 'AdjustmentsHorizontalIcon', title: 'Granular Control', desc: 'Per-branch permissions' },
                ].map((c) => (
                  <div key={c.title} className="rounded-xl p-5 border" style={{ borderColor: 'rgba(14,116,144,0.15)', background: 'rgba(14,116,144,0.04)' }}>
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0E7490] mb-2" />
                    <p className="font-semibold text-sm text-[#0F172A] mb-0.5">{c.title}</p>
                    <p className="text-xs text-[#475569]">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── VENDOR PARTNERSHIP ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(14,116,144,0.3)] bg-[rgba(14,116,144,0.08)] text-[11px] font-semibold tracking-widest text-[#0E7490] uppercase mb-4">
              Vendor Partnership
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Build a network of{' '}
              <span className="italic font-light text-[#0E7490]">verified contractors & vendors.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Extend capacity on-demand by empanelling external contractors without losing control over quality or payments.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {vendorBenefits.map((v, i) => (
              <div
                key={v.title}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-white rounded-2xl p-7 border border-[rgba(14,116,144,0.1)] hover:border-[rgba(14,116,144,0.3)] hover:shadow-md transition-all duration-300 group`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                  style={{ background: 'linear-gradient(135deg, rgba(14,116,144,0.12), rgba(8,145,178,0.06))' }}
                >
                  <Icon name={v.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0E7490]" />
                </div>
                <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{v.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0C3B47 0%, #0E7490 55%, #0891B2 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded facility platform,{' '}
              <span className="italic font-light text-[#A5F3FC]">live in 21 days.</span>
            </h2>

            <p className="relative text-white/65 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/70 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0E7490] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/65 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
